/* eslint-disable */
// Universal Global Search — portable data layer (Phase B). Pure helpers, no
// hooks. Assembles the seven entity groups independently of the active portal:
// Items (cached full fetch — eliminates the first-60 gap), Developments,
// Designers, Brands, Portals, Pages/Departments (static from PORTAL_NAV), Lenses.
// The hook (useGlobalSearchSources) wires the queries + permission gates; this
// file owns the queryFn, the registry, the builders and the matcher.
import { supabase } from '../../supabase';
import { supabaseWithRetry } from '../../utils';
import { PORTAL_NAV } from '../../constants/portals';
import { lensMatch } from '../../portals/data/lensBank/lensSearch';

// ---- Items: cached full fetch (mirror useAppData.fetchAllItemsRaw shape) ----
export const GS_ITEMS_KEY = ['global-search', 'items'];
export async function gsItemsQueryFn() {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await supabaseWithRetry(() =>
      supabase.from('items').select('*').order('created_at', { ascending: false }).range(from, from + 999));
    if (error || !data || !data.length) break;
    all = [...all, ...data];
    if (data.length < 1000) break;
    from += 1000;
  }
  return all.map(row => ({ ...row.data, id: row.id }));
}

// ---- LTD (lens developments): cached full fetch, gated by lens read access ----
// Portal-independent (own key, not ['ltd-list']) so the overlay stays complete regardless of whether
// the Lens-lab dept is mounted. Mirrors the items/lenses cached-query pattern (anchor 2 gate: no LTD
// list is loaded in the global context, so fetch on-demand rather than forcing every session to load it).
export const GS_LTD_KEY = ['global-search', 'ltd'];
export async function gsLtdQueryFn() {
  const { data, error } = await supabaseWithRetry(() =>
    supabase.from('lens_developments')
      .select('id, ltd_code, name, status, factory, promoted_lens_no, needs_review')
      .order('updated_at', { ascending: false }));
  if (error || !data) return [];
  return data;
}
// Pure mapper: lens_developments row → search entity. code = LTD403, sub = base/model + status.
export function buildLtd(rows) {
  return (rows || []).map(r => ({
    id: r.id, code: r.ltd_code, name: r.name, status: r.status,
    lens_no: r.promoted_lens_no, factory: r.factory,
  }));
}

// Convention #52 — item search field list (kept in sync with useCatalogFilters.sf).
export const ITEM_FIELDS = [
  'brand', 'sku', 'model', 'notes', 'developmentOrder',
  'gender', 'material', 'frameCategory', 'status',
  'shape1', 'shape2', 'shape3', 'assignedTo',
];

// ---- derived sources ----
function titleCase(s) { return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()); }

// Mirrors designerDisplay.normalizeDesigner intent (title-case, drop empties).
export function buildDesigners(devs) {
  const counts = {};
  for (const d of devs || []) {
    const raw = String((d.data?.designer ?? d.designer) || '').trim();
    if (!raw) continue;
    const name = titleCase(raw);
    counts[name] = (counts[name] || 0) + 1;
  }
  const max = Math.max(1, ...Object.values(counts));
  return Object.entries(counts).map(([name, devs]) => ({ name, devs, max })).sort((a, b) => b.devs - a.devs);
}

export function buildBrands(fields, items) {
  const opts = ((fields || []).find(f => f.key === 'brand') || {}).options || [];
  const counts = {};
  for (const it of items || []) { if (it.brand) counts[it.brand] = (counts[it.brand] || 0) + 1; }
  const max = Math.max(1, ...opts.map(o => counts[o] || 0));
  return opts.map(name => ({ name, items: counts[name] || 0, max })).sort((a, b) => b.items - a.items);
}

// ---- static registry: Portals + Pages (works from any portal by definition) ----
const P_ICO = {
  catalog: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
  developments: '<path d="M12 3v18"/><path d="M5 8l7-5 7 5"/><path d="M5 8v8l7 5 7-5V8"/>',
  qc: '<path d="M12 3l7 3v6c0 5-3 7-7 9-4-2-7-4-7-9V6z"/><path d="M9 12l2 2 4-4"/>',
  sales: '<path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/>',
  lensBank: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.4"/>',
  projects: '<path d="M3 7l9-4 9 4-9 4z"/><path d="M3 12l9 4 9-4"/><path d="M3 17l9 4 9-4"/>',
};
// Portal registry (mirrors the prototype's six). `gate` = useCanAccessPortal key.
const PORTALS = [
  { id: 'catalog',      name: 'Originals',        tab: 'catalog',      gate: 'catalog',      tag: 'Catalog · items',            grad: true },
  { id: 'developments', name: 'Developments',     tab: 'developments', gate: 'developments', tag: 'Design pipeline' },
  { id: 'qc',           name: 'Quality Control',  tab: 'qc',           gate: 'qc',           tag: 'QC reports & inspections' },
  { id: 'sales',        name: 'Sales',            tab: 'sales_portal', gate: 'sales',        tag: 'Analytics & Sell Sheets' },
  { id: 'lensBank',     name: 'Lens Database',    tab: 'lensBank',     gate: 'lens',         tag: 'Lenses · Lens Lab' },
  { id: 'projects',     name: 'Projects',         tab: 'projects',     gate: 'projects',     tag: 'Selection & Decision emails' },
];

const PAGE_ICO = {
  board: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M8 4v16"/>',
  archive: '<rect x="3" y="4" width="18" height="4" rx="1"/><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8"/><path d="M10 12h4"/>',
  analytics: '<path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/>',
  mailIntel: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  sellsheets: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 13a7.9 7.9 0 0 0 0-2l2-1.5-2-3.4-2.3 1a8 8 0 0 0-1.7-1l-.4-2.6h-4l-.3 2.6a8 8 0 0 0-1.8 1l-2.3-1-2 3.4 2 1.5a7.9 7.9 0 0 0 0 2l-2 1.5 2 3.4 2.3-1a8 8 0 0 0 1.8 1l.3 2.6h4l.4-2.6a8 8 0 0 0 1.7-1l2.3 1 2-3.4Z"/>',
  _def: '<path d="M4 5h16"/><path d="M4 12h16"/><path d="M4 19h10"/>',
};
// Standalone pages not represented as PORTAL_NAV subsections.
const STANDALONE_PAGES = [
  { id: 'sys:settings', name: 'Settings', crumb: 'Originals · configuration', gate: 'catalog', tab: 'settings', ico: PAGE_ICO.settings },
];

// access: { catalog, developments, qc, sales, projects, lens, mi } booleans
export function buildPortals(access) {
  return PORTALS.filter(p => access[p.gate]).map(p => ({ ...p, ico: P_ICO[p.id] }));
}

export function buildPages(access) {
  const gateOk = (key) => key === 'lens' ? access.lens : !!access[key];
  const pages = [];
  for (const p of PORTAL_NAV) {
    if (p.soon || p.headerHidden) continue;               // Orders (soon)
    if (!gateOk(p.key)) continue;
    for (const s of (p.subsections || [])) {
      if (s.miGated && !access.mi) continue;
      pages.push({
        id: `${p.key}:${s.key}`, name: s.label, crumb: p.label, gate: p.key,
        tab: s.tab, devView: s.devView, salesView: s.salesView, projectsView: s.projectsView,
        ico: PAGE_ICO[s.key] || PAGE_ICO._def,
      });
    }
  }
  for (const sp of STANDALONE_PAGES) { if (gateOk(sp.gate)) pages.push(sp); }
  return pages;
}

// ---- group meta + matcher ----
export const GROUP_META = [
  { k: 'item',     label: 'Items',              ico: '<path d="M20.6 8.4 12 3 3.4 8.4v7.2L12 21l8.6-5.4Z"/><path d="M12 21V12"/><path d="m3.4 8.4 8.6 5.2 8.6-5.2"/>' },
  { k: 'dev',      label: 'Developments',       ico: P_ICO.developments },
  { k: 'designer', label: 'Designers',          ico: '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>' },
  { k: 'brand',    label: 'Brands',             ico: '<path d="M4 8h16l-1.3 11.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8Z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>' },
  { k: 'portal',   label: 'Portals',            ico: P_ICO.catalog },
  { k: 'page',     label: 'Pages & Departments', ico: PAGE_ICO._def },
  { k: 'lens',     label: 'Lenses',             ico: P_ICO.lensBank },
  { k: 'ltd',      label: 'LTD',                ico: '<path d="M9 3h6"/><path d="M10 3v6l-5 8.5A2 2 0 0 0 7 21h10a2 2 0 0 0 2-3.5L14 9V3"/><path d="M7.5 15h9"/>' },
  { k: 'order',    label: 'Orders',             ico: '<rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/>' },
  { k: 'acetate',  label: 'Acetates',           ico: '<path d="M12 3 3 8l9 5 9-5-9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 16 9 5 9-5"/>' },
];

const inc = (v, q) => String(v || '').toLowerCase().includes(q);
function matchEntity(k, d, q) {
  if (k === 'item') return ITEM_FIELDS.some(f => inc(d[f], q)) || inc(d.shape, q);
  if (k === 'dev') { const x = d.data || d; return ['dev_number', 'designer', 'fty', 'material', 'notes'].some(f => inc(x[f], q)); }
  if (k === 'brand' || k === 'designer') return inc(d.name, q);
  if (k === 'portal') return inc(d.name, q) || inc(d.tag, q);
  if (k === 'page') return inc(d.name, q) || inc(d.crumb, q);
  if (k === 'lens') return lensMatch(d, q);
  if (k === 'ltd') return inc(d.code, q) || inc(d.name, q) || inc(d.lens_no, q);
  if (k === 'order') return [d.pi, d.item_no, d.model, d.supplier, d.collection, d.material].some((v) => inc(v, q));
  if (k === 'acetate') return [d.acetate_no, d.colour_name, d.colour_family, d.finish, ...(d.aliases || [])].some((v) => inc(v, q));
  return false;
}

// Returns ordered groups [{k,label,ico,rows,total}] for the given scope.
// No caps here — the UI (Phase C/D) slices + builds the flat nav list.
export function searchAll(sources, rawQ, scope) {
  const q = (rawQ || '').trim().toLowerCase();
  const bySrc = { item: sources.items, dev: sources.devs, designer: sources.designers, brand: sources.brands, portal: sources.portals, page: sources.pages, lens: sources.lenses, ltd: sources.ltds, order: sources.orders, acetate: sources.acetates };
  const out = [];
  for (const g of GROUP_META) {
    if (scope !== 'all' && scope !== g.k) continue;
    const src = bySrc[g.k] || [];
    // the server-answered groups arrive matched and capped, with their true total alongside
    const served = sources.serverMatched && ['item', 'dev', 'designer', 'brand', 'order', 'acetate'].includes(g.k);
    const rows = served || !q ? src : src.filter(d => matchEntity(g.k, d, q));
    if (q && !rows.length) continue;
    out.push({ k: g.k, label: g.label, ico: g.ico, rows, total: (served && sources.totals?.[g.k]) ?? rows.length });
  }
  return out;
}
